import flowSensorApi from '@/api/config/flowSensorApi'
import flowValveApi from '@/api/config/flowValveApi'
import { IWaterSource } from '@/api/config/waterSourceApi'
import Select from '@/components/form-control/Select'
import { queryKeys } from '@/constant/common'
import { useAuthContext } from '@/features/auth/context'
import { typeList } from '@/features/config/components/sidebar/HydraulicRule/FormValveView/constant'
import {
  customInlineOptionFlowValveType,
  customOptionFlowSensor,
} from '@/features/config/components/sidebar/WaterSourceView/CustomOption'
import CustomOptionFlowValve from '@/features/config/components/sidebar/WaterSourceView/CustomOptionFlowValve'
import useSearchFlowSensor from '@/features/config/hooks/hydraulic/flowSensor/useSearchFlowSensor'
import useEditFlowValve from '@/features/config/hooks/hydraulic/flowValve/useEditFlowValve'
import useSearchFlowValve from '@/features/config/hooks/hydraulic/flowValve/useSearchFlowValve'
import useSearchFlowValveId from '@/features/config/hooks/hydraulic/flowValve/useSearchFlowValveId'
import useEditWaterSource from '@/features/config/hooks/watersource/useEditWaterSource'
import { NotifyService } from '@/helpers'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { useWaterCost } from '@/hooks/useWaterCost'
import { DATA_PAGINATION } from '@/types'
import { FlowSensorType, FlowValveType } from '@/types/hydraulic'
import {
  Stack,
  TableCell,
  TableRow,
  TableRowProps,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { useQueryClient } from '@tanstack/react-query'
import { find } from 'lodash'
import { useEffect, useState } from 'react'
import { useConfigUI } from '../../context'

interface IProps extends TableRowProps {
  expandedParent?: boolean
  row: IWaterSource
  handleDeleteItem: (
    id: number | string,
    interfaceId: string,
    name: string,
  ) => void
}

const WaterSourceTableRow = (props: IProps) => {
  const { t } = useTranslation()
  const { expandedParent, row, handleDeleteItem, ...trProps } = props
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const { openSidebar, setSidebarView } = useConfigUI()
  const { interfaceId } = useAuthContext()
  const { isViewOnly } = usePermission()

  const { convertFromMetric: convertWaterCostFromMetric } = useWaterCost()

  const queryClient = useQueryClient()
  const [selectedFlowValveId, setSelectedFlowValveId] =
    useState<FlowValveType>()
  const [selectedFlowType, setSelectedFlowType] = useState({})
  const [selectedSensorId, setSelectedSensorId] = useState<FlowSensorType>()

  // Inline-edit
  const { mutate: updateWaterSource, isLoading: loadingUpdateWaterSource } =
    useEditWaterSource()

  const { data: flowValve } = useSearchFlowValveId(
    Number(interfaceId),
    Number(row.flowValveId),
  )
  const {
    mutate: updateFlowValveType,
    isLoading: loadingUpdateFlowValveType,
    isSuccess,
  } = useEditFlowValve()

  const { data: flowValveData } = useSearchFlowValve(Number(interfaceId))
  const { data: flowSensorData } = useSearchFlowSensor(Number(interfaceId))

  const toggle = () => {}

  const handleEdit = (id: string | number) => {
    // ViewOnly users cannot edit water source
    if (isViewOnly) return

    openSidebar()
    setSidebarView({
      type: 'WATERSOURCE_VIEW',
      id: String(id),
    })
  }

  const getFlowValveData =
    () => async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
      let data: DATA_PAGINATION | undefined
      data = await flowValveApi
        .getAll({
          interfaceId:Number(interfaceId),
          PageIndex,
          PageSize: PageSize || 20,
          Keyword,
        })
        .then((res) => {
          return res.data
        })
        .catch((e) => {
          NotifyService.error(e)
          return undefined
        })
      return data
    }

  const getFlowSensorData =
    () => async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
      let data: DATA_PAGINATION | undefined
      data = await flowSensorApi
        .getAll({
          interfaceId:Number(interfaceId),
          PageIndex,
          PageSize: PageSize || 20,
          Keyword,
        })
        .then((res) => {
          return res.data
        })
        .catch((e) => {
          NotifyService.error(e)
          return undefined
        })
      return data
    }

  const handleChangeFlowValve = async (valve: any) => {
    try {
      updateWaterSource({
        ...row,
        flowValveId: valve.id,
      })
      await queryClient.invalidateQueries([queryKeys.waterSource])
    } catch (e) {
      console.error(e)
    }
  }

  const handleChangeFLowSensor = async (sensor: any) => {
    try {
      updateWaterSource({
        ...row,
        flowSensorId: sensor.id,
      })
      await queryClient.invalidateQueries([queryKeys.waterSource])
    } catch (e) {
      console.error(e)
    }
  }

  const handleChangeFlowValveType = async (valveType: any) => {
    try {
      if (flowValve) {
        updateFlowValveType({
          ...flowValve,
          decoderId: flowValve.decoderId || undefined,
          type: valveType.value,
        })
        await queryClient.invalidateQueries([queryKeys.waterSource])
      }
      setSelectedFlowType(valveType)
    } catch (e) {}
  }

  useEffect(() => {
    if (!expandedParent) {
      setIsExpanded(false)
    }
  }, [expandedParent])

  useEffect(() => {
    ;(async () => {
      if (flowValveData?.list && row?.flowValveId) {
        let fv = find(
          flowValveData?.list,
          (fv: any) => fv.id === row.flowValveId,
        )
        if (fv) setSelectedFlowValveId(fv)
      }

      if (flowSensorData?.list && row.flowSensorId) {
        let fs = find(
          flowSensorData?.list,
          (fs: any) => fs.id === row.flowSensorId,
        )
        if (fs) setSelectedSensorId(fs)
      }

      let fvt = find(typeList, (fs: any) => fs.value === row.flowValveType)
      if (fvt) {
        setSelectedFlowType(fvt)
      }
    })()
  }, [row, flowValveData?.list, flowSensorData?.list])

  return (
    <TableRow {...trProps}>
      <TableCell scope="row"></TableCell>

      <TableCell>{row.id}</TableCell>

      <TableCell sx={{ borderRight: '1px solid #d7d7d7' }}>
        {row.name}
      </TableCell>

      <TableCell sx={{ borderRight: '1px solid #d7d7d7' }}>
        {/*{row.flowValveName}*/}
        <Select
          isDisabled={true}
          required
          minWidth={'100%'}
          menuPortalTarget={document.body}
          isHasMore
          filterFunc={false}
          label= {t('flowValve')}
          name="flowValveId"
          bindLabel="io"
          bindKey="id"
          placeholder="Select Flow Valve"
          // getOptions={getFlowValveData()}
          options={flowValveData?.list}
          value={selectedFlowValveId}
          components={{
            Option: CustomOptionFlowValve,
            // SingleValue: CustomInlineSingleFlowValue,
          }}
          onChange={handleChangeFlowValve}
        />
      </TableCell>

      <TableCell sx={{ borderRight: '1px solid #d7d7d7' }}>
        {/*{row.flowValveType}-{row.flowValveTypeDesription}*/}
        <Select
          isDisabled={true}
          minWidth={'100%'}
          isLoading={loadingUpdateFlowValveType}
          name="flowValveType"
          menuPortalTarget={document.body}
          label= {t('formsLabelsType')}
          filterFunc={false}
          placeholder="Change FlowValve type"
          options={typeList}
          value={selectedFlowType}
          onChange={handleChangeFlowValveType}
          components={{
            Option: customInlineOptionFlowValveType,
          }}
        />
      </TableCell>

      <TableCell sx={{ borderRight: '1px solid #d7d7d7' }}>
        {/*{row.flowSensorName}*/}

        <Box
          sx={{
            position: 'relative',
          }}
        >
          <Select
            required
            isDisabled={true}
            minWidth={'100%'}
            menuPortalTarget={document.body}
            isHasMore
            filterFunc={false}
            label= {t('flowSensor')}
            name="flowSensorId"
            bindLabel="io"
            bindKey="id"
            placeholder="Select Flow Sensor"
            // getOptions={getFlowSensorData()}
            options={flowSensorData?.list}
            value={selectedSensorId}
            components={{
              Option: customOptionFlowSensor,
              // SingleValue: customInlineSingleFlowSensor,
            }}
            onChange={handleChangeFLowSensor}
            // styles={{
            //   indicatorsContainer: (base, state) => ({
            //     ...base,
            //     display: state.isDisabled ? 'none' : base.display,
            //   }),
            // }}
          />
        </Box>
      </TableCell>

      <TableCell sx={{ borderRight: '1px solid #d7d7d7' }}>
        {convertWaterCostFromMetric(row.cost)}
      </TableCell>

      <TableCell>
        <Stack direction="row" spacing="20px">
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: '500',
              lineHeight: '100%',
              color: isViewOnly ? 'black' : 'primary.main',
              cursor: isViewOnly ? 'not-allowed' : 'pointer',
              opacity: isViewOnly ? 0.2 : 1,
            }}
            onClick={() => {
              handleEdit(row.id)
            }}
          >
            {t('actionEdit')}
          </Typography>

          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: '500',
              lineHeight: '100%',
              color: isViewOnly ? 'black' : 'grey.600',
              cursor: isViewOnly ? 'not-allowed' : 'pointer',
              opacity: isViewOnly ? 0.2 : 1,
            }}
            onClick={() => {
              handleDeleteItem(row.id, interfaceId + '', row.name)
            }}
          >
            {t('actionDelete')}
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  )
}

export default WaterSourceTableRow
